import * as React from 'react'

import {
  CaretSortIcon,
  ChevronDownIcon,
  GlobeIcon,
  OpenInNewWindowIcon,
} from '@radix-ui/react-icons'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/sotano-kit/primitives/popover'
import { useToggleState } from '~/hooks/use-toggle-state'
import {
  LabeledInput,
  LabeledInputWithPresets,
} from '~/sotano-kit/primitives/input'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { useSelectedTeamAndRepo } from '~/hooks-data/use-selected-team-and-repo'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { clsx } from 'clsx'
import { mergeRefs } from '~/lib/merge-refs'
import { useIsMemberOrHigher } from '~/hooks-data/use-user-role'
import { run } from '@basehub/utils'

import { usePreviewToken } from '~/hooks-data/preview-link/use-preview-token'
import { GitIcon } from '~/components/icons/git'
import { useSelectedBranchOrCommitQuery } from '~/lib/commits/use-selected-branch-or-commit'
import { isCommit } from '@basehub/data/replicache/index'
import { BranchBadge } from '../../../../branch/_components/badge'
import { useBranches } from '~/hooks-data/use-branches'
import { getProviderBranchUrl } from '../../../../branch/utils'
import { useSuggestedBranches } from '~/hooks-data/use-suggested-branches'
import { IconButton } from '~/sotano-kit/primitives/icon-button'

const RelevantLinks = () => {
  const { data, teamReplicache, repoReplicache } = useSelectedTeamAndRepo()
  const isMemberOrHigher = useIsMemberOrHigher()
  const { data: previewTokenData } = usePreviewToken()
  const branchOrCommit = useSelectedBranchOrCommitQuery()?.data
  const defaultBranch = useBranches().data?.find((branch) => branch.isDefault)
  const suggestedBranches = useSuggestedBranches().data
  const repository = data?.repo
  const team = data?.team
  const resolvedGit = React.useMemo(() => {
    if (!branchOrCommit) return repository?.gitRepoUrl ?? ''

    const isBranch = !isCommit(branchOrCommit)
    if (!isBranch || branchOrCommit.isDefault) {
      return repository?.gitRepoUrl ?? ''
    }

    return branchOrCommit.git?.branch ?? ''
  }, [branchOrCommit, repository?.gitRepoUrl])
  const resolvedUrl = React.useMemo(() => {
    if (!branchOrCommit) return repository?.previewUrl ?? ''

    const isBranch = !isCommit(branchOrCommit)
    if (!isBranch || branchOrCommit.isDefault) {
      return repository?.previewUrl ?? ''
    }

    return (branchOrCommit.git?.deploymentUrl || repository?.previewUrl) ?? ''
  }, [branchOrCommit, repository?.previewUrl])

  // Git repo URL
  const {
    onChange: onChangeRepositoryRepoUrl,
    value: repositoryRepoUrl,
    ref: repositoryRepoRef,
  } = useSyncedValue<HTMLInputElement>({
    setValue: async (v: string) => {
      if (!branchOrCommit) return
      const isBranch = !isCommit(branchOrCommit)
      const isCommitOrDefaultBranch = !isBranch || branchOrCommit?.isDefault
      const value = v.trim() === '' ? null : v

      if (isCommitOrDefaultBranch) {
        if (!repository || repository.gitRepoUrl === value) return

        await teamReplicache?.mutate.updateRepositoryPublicInfo({
          repoId: repository.id,
          gitRepoUrl: value,
        })
      } else {
        const _value = value ?? ''
        if (branchOrCommit.git?.branch === _value) return

        await repoReplicache?.mutate.updateBranch({
          id: branchOrCommit.id,
          isoNow: new Date().toISOString(),
          data: {
            git: {
              branch: _value,
              deploymentUrl: branchOrCommit.git?.deploymentUrl,
            },
          },
        })
      }
    },
    value: resolvedGit,
    rateLimitMs: 200,
  })

  // Website URL
  const {
    onChange: onChangeWebsiteUrl,
    value: repositoryWebsiteUrl,
    ref: repositoryWebsiteUrlRef,
  } = useSyncedValue<HTMLInputElement>({
    setValue: async (v: string) => {
      if (!branchOrCommit) return
      const isBranch = !isCommit(branchOrCommit)
      const isDefaultBranch = isBranch && (branchOrCommit?.isDefault ?? true)
      const value = v.trim() === '' ? null : v

      if (isDefaultBranch || !isBranch) {
        if (!repository || repository.gitRepoUrl === value) return

        await teamReplicache?.mutate.updateRepositoryPublicInfo({
          repoId: repository.id,
          previewUrl: value,
        })
      } else {
        const _value = value || undefined
        if (branchOrCommit.git?.deploymentUrl === _value) return

        await repoReplicache?.mutate.updateBranch({
          id: branchOrCommit.id,
          data: {
            git: {
              branch: branchOrCommit.git?.branch ?? '',
              deploymentUrl: _value,
            },
          },
          isoNow: new Date().toISOString(),
        })
      }
    },
    value: resolvedUrl,
    rateLimitMs: 200,
  })

  if (!repository || !team || !branchOrCommit) return null

  const isBranch = !isCommit(branchOrCommit)
  const isCommitOrDefaultBranch =
    !isBranch || (branchOrCommit?.isDefault ?? true)

  const repositoryRepoUrlWithProtocol = isCommitOrDefaultBranch
    ? resolvedGit
    : getProviderBranchUrl({ branch: branchOrCommit, repo: repository })
  const resolvedWebsiteUrl =
    repositoryWebsiteUrl ?? repository?.previewUrl ?? ''
  const websiteProtocolShouldBeSecure =
    !resolvedWebsiteUrl.includes('localhost')
  const websiteProtocol = websiteProtocolShouldBeSecure ? 'https://' : 'http://'
  const resolvedWebsiteUrlWithProtocol = resolvedWebsiteUrl.includes('://')
    ? resolvedWebsiteUrl
    : `${websiteProtocol}${resolvedWebsiteUrl}`
  const websiteUrl = resolvedWebsiteUrl
    ? resolvedWebsiteUrlWithProtocol
    : resolvedWebsiteUrl
  const websitePreviewUrl = `${websiteUrl}${previewTokenData?.queryParam || ''}`

  return (
    <div className="flex items-center gap-2">
      <div className="h-full whitespace-nowrap">
        {Boolean(resolvedGit.trim()) && (
          <ButtonLink
            href={repositoryRepoUrlWithProtocol}
            unstyled
            focusStyled
            rel="noopener noreferer"
            target="_blank"
            className="h-full inline-flex items-center text-dark-dim hover:text-dark-bright !-outline-offset-1 px-0.5"
            prefetch={false}
          >
            <GitIcon className="mr-1 size-[14px]" />
            {resolvedGit
              ? resolvedGit.replace(/(^\w+:|^)\/\//, '')
              : 'No repository URL set'}
          </ButtonLink>
        )}

        {run(() => {
          const gitPopoverLabel = isCommitOrDefaultBranch
            ? 'Git Repository URL'
            : 'Git Branch'

          if (isMemberOrHigher && resolvedGit) {
            return (
              <LinkPopover
                ref={repositoryRepoRef}
                onChange={onChangeRepositoryRepoUrl}
                title={gitPopoverLabel}
                value={resolvedGit}
                presets={suggestedBranches?.map((branch) => ({
                  value: branch.git?.branch ?? '',
                  title: '',
                }))}
                slotFooter={
                  <LinkPopoverFooter value={repositoryRepoUrlWithProtocol} />
                }
              >
                <Button
                  unstyled
                  focusStyled
                  className={clsx(
                    repositoryRepoUrl && 'text-dark-dim',
                    'p-1 items-center justify-center hover:text-dark-bright cursor-default !-outline-offset-1 inline-flex h-full aspect-square'
                  )}
                >
                  <ChevronDownIcon className="size-3.5" />
                </Button>
              </LinkPopover>
            )
          }

          if (!isMemberOrHigher) return null

          return (
            <LinkPopover
              ref={repositoryRepoRef}
              onChange={onChangeRepositoryRepoUrl}
              title={gitPopoverLabel}
              value={resolvedGit}
              presets={suggestedBranches?.map((branch) => ({
                value: branch.git?.branch ?? '',
                title: '',
              }))}
              slotFooter={
                <LinkPopoverFooter value={repositoryRepoUrlWithProtocol} />
              }
            >
              <Button
                unstyled
                focusStyled
                className="h-full inline-flex items-center cursor-default hover:text-dark-dim !-outline-offset-1"
              >
                <GitIcon className="mr-1 size-[14px]" />
                {gitPopoverLabel}
                <span className="h-full aspect-square inline-flex items-center justify-center">
                  <ChevronDownIcon className="size-3.5 mx-1" />
                </span>
              </Button>
            </LinkPopover>
          )
        })}
      </div>

      <div className="h-full whitespace-nowrap">
        {resolvedWebsiteUrl && (
          <ButtonLink
            href={websitePreviewUrl}
            unstyled
            focusStyled
            rel="noopener noreferer"
            target="_blank"
            className="h-full inline-flex items-center text-dark-dim hover:text-dark-bright hover:underline !-outline-offset-1 px-0.5"
            prefetch={false}
          >
            <GlobeIcon width={14} height={14} className="mr-1 shrink-0" />
            {resolvedWebsiteUrl
              ? resolvedWebsiteUrl.replace(/(^\w+:|^)\/\//, '')
              : 'No website URL set'}
          </ButtonLink>
        )}

        {run(() => {
          const deploymentPopoverLabel =
            !isBranch || isCommitOrDefaultBranch
              ? 'Website URL'
              : 'Branch Preview URL'

          if (resolvedWebsiteUrl && isMemberOrHigher) {
            return (
              <LinkPopover
                ref={repositoryWebsiteUrlRef}
                title={deploymentPopoverLabel}
                onChange={(value) => {
                  if (
                    !isCommitOrDefaultBranch &&
                    !branchOrCommit.git?.deploymentUrl &&
                    !value
                  ) {
                    return
                  }

                  onChangeWebsiteUrl(value)
                }}
                value={
                  isCommitOrDefaultBranch
                    ? {
                        shorthand: websiteUrl,
                        full: websitePreviewUrl,
                      }
                    : branchOrCommit.git?.deploymentUrl ?? ''
                }
                slotAfterTitle={
                  !isCommitOrDefaultBranch &&
                  !branchOrCommit.git?.deploymentUrl && (
                    <BranchBadge className="ml-auto">
                      Inherited from {defaultBranch?.name ?? 'default'}
                    </BranchBadge>
                  )
                }
                slotFooter={
                  isCommitOrDefaultBranch ||
                  (!isCommitOrDefaultBranch &&
                    branchOrCommit.git?.deploymentUrl) ? (
                    <LinkPopoverFooter
                      value={{
                        shorthand: websiteUrl,
                        full: websitePreviewUrl,
                      }}
                    />
                  ) : (
                    <span className="text-dark-muted text-xs min-h-[17px] break-words px-4">
                      Overriding this link will only affect the current branch.
                    </span>
                  )
                }
              >
                <Button
                  unstyled
                  focusStyled
                  className={clsx(
                    resolvedWebsiteUrl && 'text-dark-dim',
                    'p-1 items-center justify-center hover:text-dark-bright cursor-default !-outline-offset-1 inline-flex h-full aspect-square'
                  )}
                >
                  <ChevronDownIcon className="size-3.5" />
                </Button>
              </LinkPopover>
            )
          }

          if (!isMemberOrHigher) return null

          return (
            <LinkPopover
              ref={repositoryWebsiteUrlRef}
              title={deploymentPopoverLabel}
              value={websiteUrl}
              onChange={onChangeWebsiteUrl}
              slotAfterTitle={
                !isCommitOrDefaultBranch &&
                !branchOrCommit.git?.deploymentUrl && (
                  <BranchBadge className="ml-auto">
                    Inherited from {defaultBranch?.name ?? 'default'}
                  </BranchBadge>
                )
              }
              slotFooter={
                isCommitOrDefaultBranch ||
                (!isCommitOrDefaultBranch &&
                  branchOrCommit.git?.deploymentUrl) ? (
                  <LinkPopoverFooter
                    value={{
                      shorthand: websiteUrl,
                      full: websitePreviewUrl,
                    }}
                  />
                ) : (
                  <span className="text-dark-muted text-xs min-h-[17px] break-words px-4">
                    Overriding this link will only affect the current branch.
                  </span>
                )
              }
            >
              <Button
                unstyled
                focusStyled
                className="h-full inline-flex items-center cursor-default hover:text-dark-dim !-outline-offset-1"
              >
                <GlobeIcon width={14} height={14} className="mr-1 shrink-0" />
                {deploymentPopoverLabel}
                <span className="h-full aspect-square inline-flex items-center justify-center">
                  <ChevronDownIcon className="size-3.5 mx-1" />
                </span>
              </Button>
            </LinkPopover>
          )
        })}
      </div>
    </div>
  )
}

type LinkPopoverProps = {
  children: React.ReactNode
  title: string
  value: string | { full: string; shorthand: string }
  slotAfterTitle?: React.ReactNode
  slotFooter?: React.ReactNode
  presets?: React.ComponentProps<typeof LabeledInputWithPresets>['presets']
  onChange: (v: string) => void
}

export const LinkPopover = React.forwardRef<HTMLInputElement, LinkPopoverProps>(
  (
    { children, title, presets, value, slotAfterTitle, slotFooter, onChange },
    ref
  ) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null)
    const { isOn, handleOff, handleOn: toggleStateHandleOn } = useToggleState()

    const handleOffAndSave = () => {
      window.history.pushState({}, '', window.location.pathname)
      handleOff()
      if (!inputRef.current) return
      onChange(inputRef.current.value ?? '')

      return
    }

    const handleOn = () => {
      window.history.pushState({}, '', '?preview-popover=true')
      toggleStateHandleOn()
    }

    const displayUrl = typeof value === 'string' ? value : value.shorthand

    return (
      <Popover
        open={isOn}
        onOpenChange={(open) => {
          if (!open) return handleOffAndSave()
          else handleOn()
        }}
      >
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          alignOffset={4}
          sideOffset={6}
          asChild
        >
          <div className="flex flex-col !bg-dark-bg !px-0 !pt-0 pb-3 w-[400px]">
            {presets?.length ? (
              <LabeledInputWithPresets
                blocked={false}
                className="!px-4 !pt-3 pb-1 !m-0"
                size="xs"
                label={
                  <>
                    {title}
                    <span className="ml-auto">{slotAfterTitle}</span>
                  </>
                }
                name={title}
                presets={presets}
                trigger={
                  <IconButton>
                    <GitIcon />
                    <CaretSortIcon />
                  </IconButton>
                }
                onSelectPreset={(v) => {
                  if (inputRef.current) {
                    inputRef.current.value = v
                  }
                  handleOffAndSave()
                }}
                inputProps={{
                  ref: mergeRefs([inputRef, ref]),
                  defaultValue: displayUrl,
                  type: 'url',
                  size: 'sm',
                  className: 'w-full',
                  autoComplete: 'off',
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                      handleOffAndSave()
                    }
                  },
                }}
              />
            ) : (
              <LabeledInput
                blocked={false}
                className="!px-4 !pt-3 pb-1 !m-0"
                size="xs"
                label={
                  <>
                    {title}
                    <span className="ml-auto">{slotAfterTitle}</span>
                  </>
                }
                name={title}
                inputProps={{
                  ref: mergeRefs([inputRef, ref]),
                  defaultValue: displayUrl,
                  type: 'url',
                  size: 'sm',
                  className: 'w-full',
                  autoComplete: 'off',
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') handleOffAndSave()
                  },
                }}
              />
            )}

            {slotFooter}
          </div>
        </PopoverContent>
      </Popover>
    )
  }
)

const LinkPopoverFooter = ({ value }: Pick<LinkPopoverProps, 'value'>) => {
  const fullUrl = typeof value === 'string' ? value : value.full
  const displayUrl = typeof value === 'string' ? value : value.shorthand

  if (!value) return null

  return (
    <span className="text-dark-muted text-xs min-h-[17px] break-words px-4">
      <ButtonLink
        href={fullUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="break-words underline hover:text-dark-dim"
        unstyled
        focusStyled
      >
        {displayUrl.replace(/(^\w+:|^)\/\//, '')}
        <OpenInNewWindowIcon width={14} height={14} className="inline ml-1" />
      </ButtonLink>
    </span>
  )
}

export default RelevantLinks
